import styled from '@emotion/styled'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { useVocabularyData } from 'app/utils/vocabulary'
import React, { memo } from 'react'
import { useInView } from 'react-intersection-observer'

export interface Props {
  isSticky?: boolean
  languageCode: string
  onClick?: () => void
  onKeyDown?: any
}

export const Hamburger = memo(function Hamburger({
  isSticky,
  languageCode,
  onClick,
  onKeyDown,
}: Props) {
  const [animObjectRef, isAnimObjectVisible] = useInView({
    triggerOnce: true,
  })

  return (
    <Container
      aria-label="Hamburger Menu"
      className={`${isSticky ? 'is-sticky' : ''}${
        isAnimObjectVisible ? ' visible' : ''
      }`}
      dial={4}
      onClick={onClick}
      onKeyDown={onKeyDown}
      ref={animObjectRef}
      role="button"
      row
      tabIndex={0}
    >
      <Lines>
        <Line />
        <Line />
        <Line />
      </Lines>

      <Text>{useVocabularyData('menu', languageCode)}</Text>
    </Container>
  )
})

const Lines = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8;
  width: 2.25rem;
  height: 100%;
  overflow: hidden;
  position: relative;
`

const Line = styled.div`
  width: 100%;
  height: 0.0625rem;
  background: ${({ theme }) => theme.colors.variants.neutralLight1};
  position: relative;
  transition: 0.4s;
  transform-origin: right;
  transition: background-color 0.3s cubic-bezier(0.24, 0.43, 0.15, 0.97) 0s;
  &:nth-of-type(1) {
    transition: transform 0.8s cubic-bezier(0.24, 0.43, 0.15, 0.97) 0s;
  }
  &:nth-of-type(2) {
    transition: transform 0.8s cubic-bezier(0.24, 0.43, 0.15, 0.97) 0.15s;
  }
  &:nth-of-type(3) {
    transition: transform 0.8s cubic-bezier(0.24, 0.43, 0.15, 0.97) 0.3s;
  }
  &:before {
    content: '';
    background: ${({ theme }) => theme.colors.variants.neutralLight1};
    width: 100%;
    height: 0.0625rem;
    position: absolute;
    top: 0;
    left: 0;
    transform: translate(-170%);
  }
`

const Text = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralLight1};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.75rem;
  font-variation-settings: 'wght' 400;
  line-height: 0.9375rem;
  margin-left: 0.75rem;
  text-transform: uppercase;
  transform: translateY(0.125rem);

  @media (max-width: 1199px) {
    display: none;
  }
`

const Container = styled(FlexBox)`
  height: 1.25rem;
  cursor: pointer;
  opacity: 0;
  overflow: hidden;
  transform: translateY(-2.5rem);
  transition: 1s 0.5s ease-in-out;
  &:focus,
  &:focus-visible {
    transition: none !important;
  }
  &:hover {
    ${Line} {
      transform: translate(170%);
    }
  }
  &.is-sticky {
    opacity: 1 !important;
    transform: none !important;

    ${Line} {
      background: ${({ theme }) => theme.colors.variants.primaryDark};
      &:before {
        background: ${({ theme }) => theme.colors.variants.primaryDark};
      }
    }
    ${Text} {
      color: ${({ theme }) => theme.colors.variants.primaryDark};
    }
  }
  &.visible {
    opacity: 1;
    transform: none;
  }

  @media (max-width: 1199px) {
    opacity: 1 !important;
    transform: none !important;
  }
`
